import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Filter, Link2, RefreshCw, Search, ShieldCheck, Timer, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { AdminDomainTable } from "./AdminDomainTable";
import { FingerproxyGlobalStatusHeader } from "./FingerproxyGlobalStatusHeader";
import { ServerSidePanel } from "./ServerSidePanel";
import { SearchableLicensedUserCombobox } from "./SearchableLicensedUserCombobox";
import { IdleAssignmentsModal } from "./IdleAssignmentsModal";


export const DomainAssignmentWorkspace = ({ api, headers, syncResult, syncing, onSyncAll }) => {
  const authorization = headers.Authorization;
  const [workspace, setWorkspace] = useState({ domains: [], users: [], servers: [], summary: {} });
  const [diagnostics, setDiagnostics] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serverFilter, setServerFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedServer, setSelectedServer] = useState("");
  const [busy, setBusy] = useState("");
  const [idleOpen, setIdleOpen] = useState(false);

  const loadWorkspace = useCallback(async () => {
    const requestHeaders = { Authorization: authorization };
    // Load the two feeds independently: a slow diagnostics request (e.g. an
    // unreachable server causing a 10s+ SSH timeout) must never block the
    // workspace table from rendering.
    const workspacePromise = axios.get(`${api}/admin/domains/assignment-workspace`, { headers: requestHeaders });
    const diagnosticsPromise = axios.get(`${api}/admin/servers/fingerproxy-diagnostics`, { headers: requestHeaders });
    workspacePromise
      .then((workspaceResponse) => {
        setWorkspace(workspaceResponse.data);
        setSelectedServer((current) => current || workspaceResponse.data.recommended_server_id || "");
      })
      .catch(() => {});
    diagnosticsPromise
      .then((diagnosticResponse) => setDiagnostics(diagnosticResponse.data))
      .catch(() => {});
    // Resolve the outer promise as soon as the workspace feed is ready so the
    // caller's loading state ends promptly.
    await workspacePromise;
  }, [api, authorization]);

  useEffect(() => {
    loadWorkspace().catch(() => toast.error("Unable to load domain assignment workspace"));
    const interval = setInterval(() => loadWorkspace().catch(() => {}), 15000);
    return () => clearInterval(interval);
  }, [loadWorkspace]);

  const filteredDomains = useMemo(() => workspace.domains.filter((domain) => {
    const matchesQuery = !query || domain.domain.toLowerCase().includes(query.toLowerCase()) || domain.assigned_users?.some((user) => user.username.toLowerCase().includes(query.toLowerCase()));
    const matchesStatus = statusFilter === "all" || (statusFilter === "unassigned" ? (domain.raw_assignment_count ?? domain.assignment_count ?? (domain.assigned_users?.length || 0)) === 0 : domain.readiness === statusFilter);
    const matchesServer = serverFilter === "all" || domain.server_id === serverFilter;
    return matchesQuery && matchesStatus && matchesServer;
  }), [workspace.domains, query, statusFilter, serverFilter]);

  const toggleDomain = (id) => setSelected((current) => {
    const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleAll = () => setSelected((current) => filteredDomains.every((domain) => current.has(domain.id)) ? new Set() : new Set(filteredDomains.map((domain) => domain.id)));

  const runAction = async (kind) => {
    const domainIds = [...selected];
    if (!domainIds.length) return toast.error("Select at least one managed domain");
    setBusy(kind);
    try {
      if (kind === "check") {
        const response = await axios.post(`${api}/admin/domains/readiness-check`, { domain_ids: domainIds, target_server_id: selectedServer || null }, { headers });
        toast.success(`${response.data.summary.ready}/${response.data.summary.checked} domains are DNS + TLS ready`);
      } else if (kind === "assign") {
        if (!selectedUser) throw new Error("Choose a licensed user first");
        const response = await axios.post(`${api}/admin/domains/bulk-assign`, { domain_ids: domainIds, user_id: selectedUser, target_server_id: selectedServer || null }, { headers });
        toast.success(`${response.data.assigned} domain(s) assigned to ${response.data.username}`);
        if (response.data.warnings?.length) toast.warning(`${response.data.warnings.length} assigned domain(s) still need DNS or SSL attention`);
      } else {
        const response = await axios.post(`${api}/admin/domains/bulk-unassign`, { domain_ids: domainIds }, { headers });
        toast.success(`${response.data.unassigned} assignment(s) removed`);
      }
      setSelected(new Set());
      await loadWorkspace();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Domain action failed");
    } finally {
      setBusy("");
    }
  };

  const syncAll = async () => { await onSyncAll(); await loadWorkspace(); };

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-6 wx-fade-up" data-testid="domain-assignment-workspace">
      <header className="flex flex-col gap-5 border-b border-slate-800/80 pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-3">
          <span className="wx-tactical"><Link2 className="h-3 w-3" /> Admin-managed pool</span>
          <h1 className="font-orbitron text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="assignments-title">Domain Assignments</h1>
          <p className="max-w-2xl text-sm text-slate-400">Search, pre-check, place, assign, and unassign domains across the fleet.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center sm:grid-cols-6" data-testid="assignment-summary-metrics">
          {[
            { label: "Total",         value: workspace.summary.total,         tone: "text-slate-100",   accent: "border-slate-700/60" },
            { label: "Unassigned",    value: workspace.summary.unassigned,    tone: "text-amber-300",   accent: "border-amber-500/25" },
            { label: "DNS pending",   value: workspace.summary.pending_dns,   tone: "text-cyan-300",    accent: "border-cyan-500/25" },
            { label: "SSL issues",    value: workspace.summary.ssl_issues,    tone: "text-rose-300",    accent: "border-rose-500/25" },
            { label: "Not routable",  value: workspace.summary.not_routable,  tone: "text-slate-300",   accent: "border-slate-600/40" },
            { label: "Ghosts",        value: workspace.summary.ghosts,        tone: "text-fuchsia-300", accent: "border-fuchsia-500/25" },
          ].map(({ label, value, tone, accent }) => (
            <div key={label} className={`wx-card !p-3 border ${accent}`}>
              <p className={`font-mono text-xl font-bold tabular-nums ${tone}`}>{value || 0}</p>
              <p className="wx-metric-label mt-1">{label}</p>
            </div>
          ))}
        </div>
      </header>

      <FingerproxyGlobalStatusHeader diagnostics={diagnostics} syncResult={syncResult} syncing={syncing} onSync={syncAll} />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12">
        <section className="space-y-4 lg:col-span-8">
          <div className="wx-card !p-0 overflow-hidden" data-testid="domain-assignment-toolbar">
            <div className="grid gap-3 p-4 md:grid-cols-[minmax(220px,1fr)_200px_200px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search managed domains or users" className="input-dark h-10 w-full !pl-10" data-testid="domain-search-input" />
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="input-dark h-10 w-full" data-testid="filter-status-select"><Filter className="mr-2 h-4 w-4 text-slate-500" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All readiness</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="pending_dns">Pending DNS</SelectItem>
                  <SelectItem value="ssl_issue">SSL issues</SelectItem>
                  <SelectItem value="ready">DNS + TLS ready</SelectItem>
                  <SelectItem value="not_routable">Not routable</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                </SelectContent>
              </Select>
              <Select value={serverFilter} onValueChange={setServerFilter}>
                <SelectTrigger className="input-dark h-10 w-full" data-testid="filter-server-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All servers</SelectItem>
                  {workspace.servers.map((server) => <SelectItem key={server.id} value={server.id}>{server.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-3 border-t border-slate-800/70 px-4 py-4 xl:flex-row xl:items-center xl:gap-4">
              <p className="min-w-fit text-sm text-slate-200" data-testid="selected-domain-count">
                <span className="font-mono text-base font-bold tabular-nums text-cyan-300">{selected.size}</span>
                <span className="ml-1 text-slate-500">selected</span>
              </p>
              <SearchableLicensedUserCombobox users={workspace.users} value={selectedUser} onChange={setSelectedUser} />
              <div className="flex flex-wrap gap-2 xl:ml-auto">
                <button onClick={() => runAction("check")} disabled={busy || !selected.size} className="wx-btn-secondary" data-testid="bulk-readiness-check-button"><ShieldCheck className="h-4 w-4" /> Pre-check</button>
                <button onClick={() => runAction("assign")} disabled={busy || !selected.size || !selectedUser} className="wx-btn-primary" data-testid="bulk-assign-button"><Link2 className="h-4 w-4" /> Assign</button>
                <button onClick={() => runAction("unassign")} disabled={busy || !selected.size} className="wx-btn-danger" data-testid="bulk-unassign-button"><Unlink className="h-4 w-4" /> Unassign</button>
                <button type="button" onClick={() => setIdleOpen(true)} disabled={busy} className="wx-btn border border-amber-500/30 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20" data-testid="open-idle-reclaim-btn" title="Find idle assignments and free pool capacity"><Timer className="h-4 w-4" /> Reclaim idle</button>
              </div>
            </div>
            {busy && (
              <p className="flex items-center gap-2 border-t border-slate-800/70 bg-cyan-500/5 px-4 py-2 text-xs text-cyan-300" data-testid="domain-action-progress">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Running {busy} operation…
              </p>
            )}
          </div>
          <AdminDomainTable domains={filteredDomains} selected={selected} onToggle={toggleDomain} onToggleAll={toggleAll} />
        </section>
        <div className="space-y-4 lg:col-span-4">
          <ServerSidePanel servers={workspace.servers} selectedServer={selectedServer} recommendedServerId={workspace.recommended_server_id} onSelect={setSelectedServer} />
          <div className="wx-card !p-4" data-testid="assignment-readiness-note">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-100"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Pre-assignment safety</p>
            <p className="mt-2 text-xs leading-relaxed text-slate-400">Pre-check compares each A record with the selected server and validates its TLS certificate. Assignment remains possible with warnings so DNS can be staged safely.</p>
          </div>
        </div>
      </div>
      <IdleAssignmentsModal
        api={api}
        headers={headers}
        open={idleOpen}
        onClose={() => setIdleOpen(false)}
        onReclaimed={loadWorkspace}
      />
    </div>
  );
};